import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

export interface SetupState {
  currentPatientId: string | null;
  currentStep: 1 | 2 | 3 | 4;
  completedSteps: number[];
  patient: {
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string;
    gender: string;
  } | null;
  /** Cached for device assignment and later steps (from create-patient API). */
  provisioning: {
    clinicId: number;
    clinicPatientId: number;
    admissionId: number;
    /** Cognito / app user id from create response when present. */
    userId?: string;
  } | null;
  wifi: {
    ssid: string;
    confirmed: boolean;
  } | null;
  device: {
    deviceId: string;
    roomName: string;
    connected: boolean;
    credentialsSent: boolean;
    /**
     * ISO timestamp (tablet clock) of the moment Step 2 finished Wi-Fi
     * provisioning and the backend accepted the device assignment.
     *
     * Step 3 uses this as a freshness floor: any engine `updated_at` strictly
     * older than this represents a check-in from the device's *previous*
     * network state, so it must not satisfy the "device is online" check.
     *
     * Optional + nullable so old redux-persist blobs (without this field) and
     * non-setup flows (HealthOverview etc., if they ever start reading this
     * slice) don't break — Step 3 treats `null` as "fall back to the absolute
     * 5-minute threshold only", matching the pre-Phase-2 behaviour.
     */
    credentialsSentAt: string | null;
  } | null;
  verification: {
    allChecksPassed: boolean;
    failedCheck: string | null;
  } | null;
  completedAt: string | null;
  /** Preserved when `resetSetup` runs after a finished install (Complete screen / PDF). */
  completionSnapshot: {
    patient: NonNullable<SetupState["patient"]>;
    device: NonNullable<SetupState["device"]>;
    wifi: NonNullable<SetupState["wifi"]>;
    completedAt: string;
  } | null;
}

const name = "setup";

export const initialState: SetupState = {
  currentPatientId: null,
  currentStep: 1,
  completedSteps: [],
  patient: null,
  provisioning: null,
  wifi: null,
  device: null,
  verification: null,
  completedAt: null,
  completionSnapshot: null,
};

export const setupSlice = createSlice({
  name,
  initialState,
  reducers: {
    setPatient(
      state,
      action: PayloadAction<NonNullable<SetupState["patient"]>>
    ) {
      state.patient = action.payload;
    },
    setProvisioning(
      state,
      action: PayloadAction<NonNullable<SetupState["provisioning"]>>
    ) {
      state.provisioning = action.payload;
    },
    clearProvisioning(state) {
      state.provisioning = null;
    },
    setWifi(state, action: PayloadAction<NonNullable<SetupState["wifi"]>>) {
      state.wifi = action.payload;
    },
    setDevice(state, action: PayloadAction<NonNullable<SetupState["device"]>>) {
      state.device = action.payload;
    },
    setVerification(
      state,
      action: PayloadAction<NonNullable<SetupState["verification"]>>
    ) {
      state.verification = action.payload;
    },
    setCurrentStep(state, action: PayloadAction<SetupState["currentStep"]>) {
      state.currentStep = action.payload;
    },
    markStepComplete(state, action: PayloadAction<number>) {
      const step = action.payload;
      if (!state.completedSteps.includes(step)) {
        state.completedSteps.push(step);
      }
    },
    resetSetup(state) {
      const snapshot =
        state.patient && state.device && state.wifi && state.completedAt
          ? {
              patient: state.patient,
              device: state.device,
              wifi: state.wifi,
              completedAt: state.completedAt,
            }
          : null;
      return {
        ...initialState,
        completionSnapshot: snapshot,
        provisioning: null,
      };
    },
    setCurrentPatientId(state, action: PayloadAction<string | null>) {
      state.currentPatientId = action.payload;
      state.completionSnapshot = null;
      if (!action.payload) {
        state.provisioning = null;
      }
    },
    setCompletedAt(state, action: PayloadAction<string | null>) {
      state.completedAt = action.payload;
    },
  },
});

export const {
  setPatient,
  setProvisioning,
  clearProvisioning,
  setWifi,
  setDevice,
  setVerification,
  setCurrentStep,
  markStepComplete,
  resetSetup,
  setCurrentPatientId,
  setCompletedAt,
} = setupSlice.actions;

const persistedReducer = persistReducer(
  {
    key: name,
    storage,
    whitelist: [
      "currentPatientId",
      "currentStep",
      "completedSteps",
      "patient",
      "provisioning",
      "wifi",
      "device",
      "verification",
      "completedAt",
      "completionSnapshot",
    ],
  },
  setupSlice.reducer
);

export default persistedReducer;
